import { useNavigate, Link } from "react-router-dom";
import { Mic, Send, Sparkles, Gift, ShieldCheck, ChevronRight, Heart } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Waveform } from "@/components/Waveform";
import { BrandMark, BrandLogo } from "@/components/BrandMark";
import { DemoStory } from "@/components/DemoStory";
import { PriceSection } from "@/components/PriceSection";
import { useAuth } from "@/context/AuthContext";
import { ASSETS } from "@/lib/assets";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const go = () => navigate(user ? "/dashboard" : "/signin");

  const steps = [
    { icon: Heart, title: "Add your family", text: "Add the children, grandchildren, relatives or people whose memories matter to you." },
    { icon: Mic, title: "Record your memories", text: "Record a personal message for each person. Start with the first time you met them or a moment you never want forgotten." },
    { icon: Send, title: "Invite their memories", text: "Each person receives a private invitation to hear your memory and record one of their earliest memories of you." },
    { icon: Sparkles, title: "Receive your family stories", text: "Your recordings become My Memories of You. Their replies become Your Memories of Me." },
  ];

  return (
    <div className="min-h-screen legacy-bg text-[#EDE6F7] overflow-x-hidden">
      <Navbar />

      <section className="relative overflow-hidden">
        <img src={ASSETS.heroFamily} alt="" aria-hidden="true" data-testid="hero-family-photo"
          className="absolute inset-0 w-full h-full object-cover object-[70%_30%] opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#120B19]/40 via-[#120B19]/70 to-[#120B19]" />
        <div className="absolute inset-x-0 top-0 h-1/2 bg-[radial-gradient(60%_80%_at_50%_0%,rgba(139,44,255,0.22),transparent_70%)]" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-6 pt-20 pb-24 text-center">
          <div className="flex justify-center fade-up"><BrandLogo className="h-28 sm:h-40 lg:h-48 max-w-full" /></div>
          <p className="font-cormorant italic text-2xl sm:text-3xl lg:text-4xl text-[#B57BFF] mt-1 mb-10 fade-up" data-testid="hero-slogan">The Voices We Love</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.12] tracking-tight fade-up">
            Keep the voices you love,<br className="hidden sm:block" />{" "}one memory at a time.
          </h1>
          <div className="my-9 opacity-80 fade-up"><Waveform bars={56} height={54} /></div>
          <p className="text-lg sm:text-xl text-[#C9C2D8] max-w-2xl mx-auto leading-relaxed fade-up">
            Record your memories of someone special. Invite them to share theirs in return. Keep the stories, laughter
            and voices that make your family yours.
          </p>
          <p className="mt-6 font-cormorant italic text-2xl text-[#D9C6FF] fade-up">
            One family. Two sides of the story.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center fade-up">
            <button data-testid="hero-start-btn" onClick={go}
              className="gold-btn h-16 px-10 rounded-full text-xl flex items-center justify-center">
              Record your first memory <ChevronRight className="h-6 w-6 ml-1" />
            </button>
            <button data-testid="hero-how-btn" onClick={() => document.getElementById("how")?.scrollIntoView()}
              className="ghost-gold h-16 px-10 rounded-full text-xl">
              See How It Works
            </button>
          </div>
          <p className="mt-6 text-sm text-[#A49CB8] tracking-wide fade-up" data-testid="hero-trust-line">
            Private by default · Your family's real voices
          </p>
          <p className="mt-3 text-base text-[#B57BFF] fade-up" data-testid="hero-price-line">
            <a href="#price" className="underline underline-offset-4 hover:text-[#EDE6F7]">£9.99 one-off payment</a> · No subscription
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6"><div className="gold-line" /></div>


      {/* One family, two stories */}
      <section className="border-b border-[#2E2140] bg-[#120B19]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto">
            <p className="eyebrow mb-3">One family. Two stories.</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#EDE6F7] leading-tight">
              One gift becomes two stories.
            </h2>
            <p className="mt-5 text-lg text-[#C9C2D8] leading-relaxed">
              You begin by sharing your memories of them. Quietly, their memories of you begin coming back.
            </p>
          </div>

          {/* Two waveforms converging on the stag mark */}
          <div className="flex items-center justify-center gap-4 sm:gap-6 my-14" aria-hidden="true">
            <div className="flex-1 max-w-[180px] sm:max-w-[240px] [mask-image:linear-gradient(to_right,transparent,#0E0A14)]">
              <Waveform bars={30} height={40} />
            </div>
            <div className="relative shrink-0">
              <div className="absolute inset-0 rounded-full bg-[#7C3AED]/15 blur-2xl scale-150" />
              <BrandMark size={56} className="relative" />
            </div>
            <div className="flex-1 max-w-[180px] sm:max-w-[240px] [mask-image:linear-gradient(to_left,transparent,#0E0A14)]">
              <Waveform bars={30} height={40} />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="glass-card p-8 sm:p-10 flex flex-col" data-testid="story-card-yours">
              <p className="eyebrow mb-3">Your story</p>
              <h3 className="font-serif text-3xl text-[#EDE6F7] mb-4">My Memories of You</h3>
              <p className="text-[#C9C2D8] text-lg leading-relaxed flex-1">
                Your voice. Your memories. The first time you met them, the moments you remember and the things you
                never want your family to forget.
              </p>
              <div className="gold-line my-6" />
              <p className="text-sm text-[#A49CB8]">Created from your original recordings.</p>
            </div>

            <div className="glass-card p-8 sm:p-10 flex flex-col" data-testid="story-card-theirs">
              <p className="eyebrow mb-3">Their story</p>
              <h3 className="font-serif text-3xl text-[#EDE6F7] mb-4">Your Memories of Me</h3>
              <p className="text-[#C9C2D8] text-lg leading-relaxed flex-1">
                Each person is privately invited to share one of their earliest memories of you. Their replies are
                brought together as a second family story — created for you.
              </p>
              <div className="gold-line my-6" />
              <p className="text-sm text-[#A49CB8]">A surprise told in the voices of the people you love.</p>
            </div>
          </div>
        </div>
      </section>


      <section id="how" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <p className="eyebrow text-center mb-3">How it works</p>
        <h2 className="font-serif text-3xl sm:text-4xl text-center mb-16">Four gentle steps</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="glass-card p-8 sm:p-9" data-testid={`how-step-${i + 1}`}>
              <div className="h-16 w-16 rounded-full bg-[#1A1222] border border-[rgba(139,44,255,0.3)] flex items-center justify-center mb-6">
                <s.icon className="h-8 w-8 text-[#B57BFF]" />
              </div>
              <p className="eyebrow mb-2">Step {i + 1}</p>
              <h3 className="font-serif text-2xl sm:text-[1.75rem] mb-4 leading-snug">{s.title}</h3>
              <p className="text-[#D2CBE0] text-lg sm:text-xl leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      <DemoStory />

      {/* Your voice stays your voice */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="eyebrow mb-3">A promise</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#EDE6F7] leading-tight">
            Your voice stays your voice.
          </h2>
          <p className="mt-5 text-lg text-[#C9C2D8] leading-relaxed">
            Memories Forever is built around the recordings your family actually makes. It is designed not to invent
            memories or family facts — AI only helps organise what your family provides, you review and approve it,
            and a loved one's voice is never replaced with an artificial version.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { title: "Real voices", text: "The recordings you hear come from the people who made them." },
            { title: "Real memories", text: "Only memories and information provided or approved by your family belong in the finished story." },
            { title: "Gentle organisation", text: "Technology can help organise recordings into chapters and create transitions, but the family's memories remain the story." },
          ].map((p, i) => (
            <div key={i} className="glass-card p-8" data-testid={`trust-principle-${i}`}>
              <div className="h-8 w-8 rounded-full border border-[rgba(139,44,255,0.4)] flex items-center justify-center mb-5">
                <span className="gold-text text-sm">{i + 1}</span>
              </div>
              <h3 className="font-serif text-2xl text-[#EDE6F7] mb-3">{p.title}</h3>
              <p className="text-[#C9C2D8] leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-[#A49CB8] mt-10 max-w-2xl mx-auto leading-relaxed">
          Your original keepsake is made only from your family's original recordings — nothing is rewritten or
          replaced with synthetic speech. A separate, optional narrated-story upgrade (coming soon) will use
          AI-recreated voices only with each speaker's own explicit consent.
        </p>
      </section>


      <PriceSection onStart={go} />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <ShieldCheck className="h-9 w-9 text-[#B57BFF] mx-auto mb-6" />
          <p className="eyebrow mb-3">Private by default</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#EDE6F7] leading-tight">
            Your family memories aren't social media.
          </h2>
          <p className="mt-5 text-lg text-[#C9C2D8] leading-relaxed">
            Memories Forever is designed for the people you choose — not followers, feeds or public profiles.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {[
            { title: "Private invitations", text: "Family members join through private invitations rather than public profiles." },
            { title: "You choose your family", text: "Only the people you invite can contribute to or open your family's recordings." },
            { title: "Contributor consent", text: "Everyone confirms how their recording will be used before they send it." },
            { title: "Your memories, your control", text: "You can remove any chapter and its recordings, and download your finished story whenever you like." },
          ].map((p, i) => (
            <div key={i} className="glass-card p-8" data-testid={`privacy-principle-${i}`}>
              <h3 className="font-serif text-2xl text-[#EDE6F7] mb-3">{p.title}</h3>
              <p className="text-[#C9C2D8] leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <button data-testid="footer-start-btn" onClick={go}
            className="gold-btn h-16 px-10 rounded-full text-xl">
            Record your first memory
          </button>
          <p className="mt-6">
            <Link to="/privacy" data-testid="privacy-link" className="text-[#A49CB8] underline hover:text-[#B57BFF] transition-colors">
              Read how we handle your recordings
            </Link>
          </p>
        </div>
      </section>

      <footer className="border-t border-[#2E2140] py-12 text-center">
        <p className="font-serif text-lg tracking-[0.3em] text-[#EDE6F7]">MEMORIES FOREVER</p>
        <p className="font-cormorant italic text-lg text-[#B57BFF] mt-1">The Voices We Love</p>
        <p className="mt-3"><Link to="/privacy" className="text-[#8C84A0] text-sm underline hover:text-[#A49CB8]">Privacy &amp; your control</Link></p>
      </footer>
    </div>
  );
}
